import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useFillBlankQuiz } from "../hooks/use-fill-blank-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function FillBlankStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  useKeypad: _useKeypad = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    blankCount,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleSubmit
  } = useFillBlankQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (blankCount === 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      (index, content) => {
        var _a2, _b;
        const key = content || "";
        const value = answers[index] ?? "";
        const hasValue = value.length > 0;
        const isActive = currentIndex === index;
        const isCorrect = isSolved ? ((_a2 = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a2[index]) ?? null : null;
        const solved = isSolved || isViewSolved;
        if (solved) {
          const correctAnswer = ((_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index]) ?? "";
          const displayValue = shouldShowCorrectAnswers && isCorrect === false ? correctAnswer : value || "?";
          return /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "inline-flex items-center justify-center px-4 h-12 align-middle rounded-2xl mx-2 text-[24px] font-medium transition-all text-center border-none [box-shadow:unset] pointer-events-none",
                isCorrect === true && "bg-[#D8F6EF] text-[#3BD0AE]",
                isCorrect === false && !shouldShowCorrectAnswers && "bg-[#FFE1DF] text-[#FF675E]",
                isCorrect === false && shouldShowCorrectAnswers && "bg-[#D8F6EF] text-[#3BD0AE]",
                isCorrect === null && "bg-[#DDF3FF] text-gray-900"
              ),
              children: displayValue
            },
            `blank-${quizId}-${index}`
          );
        }
        return /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value,
            onChange: (e) => handleInputChange(index, e.target.value, key),
            onClick: () => handleBlankClick(index),
            placeholder: "?",
            className: cn(
              "inline-flex items-center justify-center px-4 h-12 align-middle rounded-2xl mx-2 text-[24px] font-medium text-gray-900 transition-all text-center outline-hidden",
              // Empty, not active
              !isActive && !hasValue && [
                "border-2 border-[#BBBBBB] [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)]",
                "hover:text-[#55C3FF] hover:border-[#55C3FF] hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]"
              ],
              // Active
              isActive && "border-2 border-[#55C3FF] bg-[#DDF3FF] min-w-[96px]",
              // Active with value
              isActive && hasValue && "[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]",
              // Has value, not active
              !isActive && hasValue && "bg-[#DDF3FF]"
            ),
            style: { width: Math.max(value.length * 16, 56) + "px" }
          },
          `blank-${quizId}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [blankCount, currentIndex, isSolved, isViewSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, handleBlankClick, handleInputChange, answers, quiz.additionalImages]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.fillBlank.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "text-2xl font-medium text-gray-900", children: renderedProblem }),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? (_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.join(", ") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  FillBlankStudent
};
//# sourceMappingURL=fill-blank-student.js.map
